import { supabase, getAccessToken } from './supabase';
import { getProfileUserId } from '../utils/identityUtils';
import { isFullTier } from '../utils/userTier';
import { SOURCE_BRANCH } from '../utils/deployInfo';
import { logEvent } from './analytics/eventLogger';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const PROMPT_SEEN_KEY = 'notification_prompt_seen';

/**
 * Convert VAPID key from base64url to Uint8Array (required by PushManager.subscribe)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get current notification permission state
 * @returns {'granted' | 'denied' | 'default'}
 */
export function getPermissionState() {
  if (!isPushSupported()) return 'denied';
  return Notification.permission;
}

/**
 * Whether the notification pre-prompt has ever been answered on this device.
 * The one deliberate ask: set on any response (granted/denied/dismissed),
 * never re-prompt while present.
 */
export function hasSeenNotificationPrompt() {
  return localStorage.getItem(PROMPT_SEEN_KEY) === 'true';
}

/**
 * Mark the pre-prompt as answered — the caller closes it, this makes it permanent.
 */
export function markNotificationPromptSeen() {
  localStorage.setItem(PROMPT_SEEN_KEY, 'true');
}

/**
 * Log a "Not now" dismissal (analytics only — re-prompting is governed by the seen flag).
 */
export function logPromptDismissed() {
  logEvent('notification_prompt_dismissed', { profile_user_id: getProfileUserId() });
}

/**
 * Register the combined service worker (push + app shell) at scope '/'.
 * Idempotent — register() with the same URL/scope returns the existing
 * registration, so this is safe to call from the early bootstrap (all users)
 * and again from registerAndSubscribe(). Separated from subscription so the
 * shell SW registers for everyone, not just users who grant notifications.
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/push-sw.js', { updateViaCache: 'none' });
  } catch (err) {
    console.error('[SW] Registration failed:', err);
    return null;
  }
}

/**
 * Register the push service worker and subscribe to push notifications.
 * Called after browser grants permission, or silently on app load when already granted.
 * @returns {Promise<boolean>} true if subscription was saved successfully
 */
export async function registerAndSubscribe() {
  if (!isPushSupported() || !VAPID_PUBLIC_KEY) return false;

  try {
    const registration = await ensureServiceWorker();
    if (!registration) return false;
    await navigator.serviceWorker.ready;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    return await saveSubscription(subscription);
  } catch (err) {
    console.error('[PUSH] Registration/subscription failed:', err);
    return false;
  }
}

/**
 * Request notification permission, then register and subscribe.
 * @returns {'granted' | 'denied' | 'default'} the resulting permission state
 */
export async function requestPermissionAndSubscribe() {
  if (!isPushSupported()) return 'denied';

  const result = await Notification.requestPermission();
  const profileUserId = getProfileUserId();

  if (result === 'granted') {
    logEvent('notification_permission_granted', { profile_user_id: profileUserId });
    await registerAndSubscribe();
  } else if (result === 'denied') {
    logEvent('notification_permission_denied', { profile_user_id: profileUserId });
  }

  return result;
}

/**
 * Update app_focused_at on push_subscriptions for server-side notification suppression.
 * Visible: sets timestamp via Supabase client. Hidden: sets null via raw fetch with keepalive
 * (survives iOS page suspension). No-op for non-profile users or users without push permission.
 */
export function updateFocusState(focused) {
  const profileUserId = getProfileUserId();
  if (!profileUserId || getPermissionState() !== 'granted') return;

  if (focused) {
    // Visible — normal Supabase client is fine, page is active
    supabase
      .from('push_subscriptions')
      .update({ app_focused_at: new Date().toISOString() })
      .eq('profile_user_id', profileUserId)
      .then(({ error }) => {
        if (error) console.error('[PUSH] Focus state update failed:', error);
      });
  } else {
    // Hidden — raw fetch with keepalive to survive iOS page suspension
    // Under this person's own session: only the owner may change their push
    // row (open-access lockdown) — no session, no update.
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const token = getAccessToken();
    if (!supabaseUrl || !supabaseKey || !token) return;

    fetch(`${supabaseUrl}/rest/v1/push_subscriptions?profile_user_id=eq.${profileUserId}`, {
      method: 'PATCH',
      headers: {
        apikey: supabaseKey,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify({ app_focused_at: null }),
      keepalive: true,
    }).catch((err) => console.error('[PUSH] Hidden focus state update failed:', err));
  }
}

/**
 * Refresh app_focused_at timestamp to keep it current during active chatting.
 * Fire-and-forget — called from sendMessage() with no await.
 * No-op for non-profile users.
 */
export function refreshFocusTimestamp() {
  updateFocusState(true);
}

/**
 * Clear PWA app badge and reset server-side badge count.
 * Handles both client badge (navigator API) and server counter (push_subscriptions).
 * Fire-and-forget — no await needed.
 */
export function clearBadge() {
  if (navigator.clearAppBadge) {
    navigator.clearAppBadge();
  }
  const profileUserId = getProfileUserId();
  if (!profileUserId) return;
  supabase
    .from('push_subscriptions')
    .update({ badge_count: 0 })
    .eq('profile_user_id', profileUserId)
    .then(
      () => {},
      (err) => console.warn('[BADGE] Failed to reset server counter:', err)
    );
}

/**
 * Save push subscription to Supabase push_subscriptions table.
 * Finds existing row by endpoint to handle subscription refreshes on same device.
 */

async function saveSubscription(subscription) {
  const profileUserId = getProfileUserId();
  // Full-only: push is a Full feature — belt-and-braces here so no subscribe
  // route (prompt, settings, future surfaces) can enroll a Simple profile.
  if (!profileUserId || !isFullTier()) return false;

  const subscriptionJson = subscription.toJSON();
  const endpoint = subscriptionJson.endpoint;

  // Check for existing subscription with same endpoint (same device, refreshed keys)
  const { data: existing } = await supabase
    .from('push_subscriptions')
    .select('id')
    .eq('profile_user_id', profileUserId)
    .eq('subscription->>endpoint', endpoint)
    .maybeSingle();

  const row = {
    profile_user_id: profileUserId,
    subscription: subscriptionJson,
    device_label: navigator.userAgent.slice(0, 100),
    source_branch: SOURCE_BRANCH,
    updated_at: new Date().toISOString(),
  };

  let error;
  if (existing) {
    // Update existing subscription (keys may have changed on refresh)
    ({ error } = await supabase.from('push_subscriptions').update(row).eq('id', existing.id));
  } else {
    // New device/subscription
    ({ error } = await supabase.from('push_subscriptions').insert(row));
  }

  if (error) {
    console.error('[PUSH] Failed to save subscription:', error);
    return false;
  }

  console.log('[PUSH] Subscription saved successfully');
  return true;
}
